import React, { useState } from 'react';
import { Select, Card, Table, Button, Space } from 'antd';
import {
  FileDoneOutlined,
  WalletOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { LoadingState, ApiErrorState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cfoService from '../../Services/cfoService';
import {
  InvoiceStatusLabels,
  InvoiceStatusColors,
  InvoicePaymentMethodLabels,
  labelOf,
} from '../../Constants/enums';
import { money, formatDate } from '../../Utils/format';

const planText = (r) => r.lines?.[0]?.description || '—';
const methodText = (v) => (v === null || v === undefined ? 'Stripe' : labelOf(InvoicePaymentMethodLabels, v));

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'PKR', 'AED'];
const PERIOD_OPTIONS = [
  { value: 'MonthToDate', label: 'Month to date' },
  { value: 'QuarterToDate', label: 'Quarter to date' },
  { value: 'YearToDate', label: 'Year to date' },
  { value: 'AllTime', label: 'All time' },
];
const GREEN = { color: '#599c16', tint: 'rgba(89,156,22,0.10)' };
const AMBER = { color: '#b54708', tint: 'rgba(247,144,9,0.12)' };
const NEUTRAL = { color: '#475467', tint: 'rgba(102,112,133,0.10)' };
const RED = { color: '#b42318', tint: 'rgba(240,68,56,0.12)' };

const CfoDashboard = () => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState('USD');
  const [period, setPeriod] = useState('MonthToDate');
  const { data, loading, error, reload } = useApiData(
    () => cfoService.getDashboard(currency, period),
    [currency, period]
  );
  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || '';
  const invoices = useApiData(() => cfoService.getInvoices({ pageSize: 8 }), []);
  const cur = data?.currency || currency;
  const overdue = data?.overdueCount ?? 0;

  const tiles = [
    { title: 'Billed', value: money(data?.billed ?? 0, cur), icon: <FileDoneOutlined />, ...GREEN },
    { title: 'Collected', value: money(data?.collected ?? 0, cur), icon: <WalletOutlined />, ...GREEN },
    { title: 'Outstanding', value: money(data?.outstanding ?? 0, cur), icon: <ExclamationCircleOutlined />, ...AMBER },
    { title: 'Overdue', value: overdue, icon: <WarningOutlined />, ...(overdue > 0 ? RED : NEUTRAL) },
  ];

  const columns = [
    { title: 'Invoice', dataIndex: 'invoiceNumber' },
    { title: 'Client', dataIndex: 'clientName' },
    { title: 'Plan', key: 'plan', render: (_, r) => planText(r) },
    { title: 'Amount', dataIndex: 'total', render: (v, r) => money(v, r.currency) },
    { title: 'Issued', dataIndex: 'issueDate', render: formatDate },
    { title: 'Method', dataIndex: 'paymentMethod', render: methodText },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <StatusTag value={v} labels={InvoiceStatusLabels} colors={InvoiceStatusColors} />,
    },
  ];

  return (
    <div>
      <WelcomeBanner
        icon={<WalletOutlined />}
        extra={
          <Space>
            <Select value={period} onChange={setPeriod} options={PERIOD_OPTIONS} style={{ width: 150 }} />
            <Select
              value={currency}
              onChange={setCurrency}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              style={{ width: 110 }}
            />
          </Space>
        }
      />

      <div style={{ color: '#667085', fontSize: 13, marginBottom: 12 }}>
        Billed &amp; Collected cover <strong>{periodLabel}</strong>. Outstanding &amp; Overdue are current point-in-time.
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : (
        <div className="stat-grid">
          {tiles.map((t) => (
            <div key={t.title} className="stat-tile">
              <div className="icon" style={{ background: t.tint, color: t.color }}>{t.icon}</div>
              <div className="label">{t.title}</div>
              <div className="value">{t.value}</div>
            </div>
          ))}
        </div>
      )}

      <Card
        title="Invoices"
        size="small"
        style={{ marginTop: 16 }}
        extra={
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate('/cfo/invoices?action=create')}>
            Create invoice
          </Button>
        }
      >
        <Table
          rowKey="id"
          size="small"
          loading={invoices.loading}
          columns={columns}
          dataSource={invoices.data?.items || []}
          pagination={false}
          onRow={(r) => ({ onClick: () => navigate(`/cfo/invoices/${r.id}`), style: { cursor: 'pointer' } })}
        />
      </Card>
    </div>
  );
};

export default CfoDashboard;
